if (!customElements.get('njt-smart-search')) {
  const recentSearches = new Map();
  customElements.define('njt-smart-search', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.events = new AbortController();
      const options = {signal:this.events.signal};
      this.input = this.querySelector('[name=q]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('.njt-smart-search__dropdown');
      this.list = this.querySelector('[role=listbox]');
      this.status = this.querySelector('[role=status]');
      this.all = this.querySelector('[data-all]');
      this.active = -1;
      this.serial = this.serial || 0;
      this.input.addEventListener('input', () => {
        this.cancel();
        this.hide();
        if (this.input.value.trim()) {
          const key = this.dataset.endpoint + '|' + this.input.value.trim().toLowerCase();
          const cached = recentSearches.get(key);
          if (cached && Date.now() - cached.time < 60000) this.search();
          else this.timer = setTimeout(() => this.search(), 120);
        }
      }, options);
      this.viewportChanged = () => {
        if (document.activeElement === this.input) this.fitResults(false);
      };
      window.visualViewport?.addEventListener('resize', this.viewportChanged, options);
      this.input.addEventListener('focus', () => {
        this.fitResults(true);

        if (this.input.value.trim()) { this.cancel(); this.search(); }
      }, options);
      this.input.addEventListener('keydown', event => this.key(event), options);
      this.list.addEventListener('pointerdown', event => {
        this.selectionPointer = {x:event.clientX, y:event.clientY};
      }, options);
      this.list.addEventListener('click', event => {
        const link = event.target.closest('a');
        const selection = window.getSelection();
        const start = this.selectionPointer;
        this.selectionPointer = null;
        // Releasing a text-selection drag must not open the product.
        if (event.detail > 0 && start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 3 &&
            link && selection && !selection.isCollapsed &&
            link.contains(selection.anchorNode) && link.contains(selection.focusNode)) {
          event.preventDefault();
        }
      }, options);
      this.querySelector('[data-close]').addEventListener('click', () => {
        this.input.focus();
        this.cancel();
        this.hide();
      }, options);
      this.addEventListener('focusout', () => setTimeout(() => {
        if (!this.contains(document.activeElement)) { this.cancel(); this.hide(); }
      }, 0), options);
      this.outside = event => {
        if (!this.contains(event.target)) { this.cancel(); this.hide(); }
      };
      document.addEventListener('pointerdown', this.outside, options);
      this.form.addEventListener('submit', event => {
        if (!this.input.value.trim()) event.preventDefault();
      }, options);
    }
    disconnectedCallback() {
      this.events?.abort();
      this.cancel();
      clearTimeout(this.layoutTimer);
      window.visualViewport?.removeEventListener('resize', this.viewportChanged);
      document.removeEventListener('pointerdown', this.outside);
      this.ready = false;
    }
    fitResults(reposition = false) {
      if (this.closest('.njt-header-search')) return;
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const height = viewport?.height || window.innerHeight;
      const header = document.querySelector('.section-header');
      const sticky = header && ['sticky', 'fixed'].includes(getComputedStyle(header).position);
      const headerHeight = sticky ? header.getBoundingClientRect().height : 0;
      const target = top + headerHeight + 12;
      const rect = this.input.getBoundingClientRect();
      const mobile = matchMedia('(max-width:749px)').matches;
      if (reposition && (mobile || top + height - rect.bottom < 380) && rect.top > target + 8) {
        window.scrollBy({top:rect.top - target, behavior:'instant'});
      }
      const bottom = this.input.getBoundingClientRect().bottom;
      this.panel.style.maxHeight = Math.max(80, top + height - bottom - 16) + 'px';
      this.panel.style.overflowY = 'auto';
    }
    cancel() {
      clearTimeout(this.timer);
      this.status?.classList.add('visually-hidden');
      this.removeAttribute('aria-busy');
      this.controller?.abort();
      this.serial++;
    }
    hide() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
      this.status.textContent = '';
      this.status.classList.add('visually-hidden');
      this.removeAttribute('aria-busy');
    }
    searchURL(term) {
      const url = new URL(this.form.action, location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      return url.href;
    }
    safeURL(raw, fallback) {
      try {
        const url = new URL(raw, location.origin);
        if (url.origin === location.origin && ['https:', 'http:'].includes(url.protocol)) return url.href;
      } catch (_) {}
      return fallback;
    }
    async search() {
      const term = this.input.value.trim();
      if (!term) return;
      const key = this.dataset.endpoint + '|' + term.toLowerCase();
      const cached = recentSearches.get(key);
      if (cached && Date.now() - cached.time < 60000) { this.render(cached.items, term); return; }
      const serial = ++this.serial;
      this.controller = new AbortController();
      this.status.textContent = this.dataset.loading;
      this.status.classList.add('visually-hidden');
      this.setAttribute('aria-busy', 'true');
      const url = new URL(this.dataset.endpoint + '.json', location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'query,product');
      url.searchParams.set('resources[limit]', '3');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][fields]', 'title,product_type,vendor,variants.title,variants.sku,variants.barcode');
      try {
        const response = await fetch(url.href, {signal: this.controller.signal});
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        if (serial !== this.serial || this.input.value.trim() !== term) return;
        const results = data.resources?.results || {};
        const queries = (results.queries || []).slice(0, 3).map(item => ({title:item.text, url:this.searchURL(item.text)}));
        const products = (results.products || []).slice(0, 3).map(item => ({title:item.title, url:this.safeURL(item.url, this.searchURL(term)), product:true, price:item.price, image:item.featured_image?.url || item.featured_image || item.image?.url || item.image}));
        const items = [...queries, ...products].filter(item => item.title);
        if (recentSearches.size >= 40) recentSearches.delete(recentSearches.keys().next().value);
        recentSearches.set(key, {time:Date.now(), items});
        this.render(items, term);
      } catch (error) {
        if (serial === this.serial) this.hide();
      }
    }
    render(items, term) {
      this.status.classList.add('visually-hidden');
      this.removeAttribute('aria-busy');
      this.list.replaceChildren();
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      if (!items.length) { this.hide(); return; }
      let productHeading = false;
      items.forEach((item, index) => {
        const li = document.createElement('li');
        li.id = this.list.id + '-' + index;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        const link = document.createElement('a');
        link.href = item.url;
        link.draggable = false;
        const label = document.createElement('span');
        label.className = 'njt-search-result-text';
        const start = item.title.toLowerCase().indexOf(term.toLowerCase());
        if (start >= 0) {
          label.append(item.title.slice(0, start));
          const strong = document.createElement('strong');
          strong.textContent = item.title.slice(start, start + term.length);
          label.append(strong, item.title.slice(start + term.length));
        } else label.textContent = item.title;
        if (item.product) {
          li.classList.add('njt-search-product');
          if (!productHeading) {
            const heading = document.createElement('span');
            heading.className = 'njt-search-products-heading';
            heading.setAttribute('aria-hidden', 'true');
            heading.textContent = this.dataset.productLabel;
            li.append(heading);
            productHeading = true;
          }
          const thumb = document.createElement('span');
          thumb.className = 'njt-search-thumb';
          thumb.setAttribute('aria-hidden', 'true');
          thumb.textContent = '▧';
          if (typeof item.image === 'string' && item.image) {
            try {
              const src = new URL(item.image, location.origin);
              if (src.protocol === 'https:' && (src.hostname === location.hostname || src.hostname.endsWith('.shopify.com'))) {
                src.searchParams.set('width', '100');
                const img = document.createElement('img');
                img.src = src.href;
                img.alt = '';
                img.width = 50;
                img.height = 50;
                img.draggable = false;
                img.addEventListener('error', () => { thumb.textContent = '▧'; });
                thumb.replaceChildren(img);
              }
            } catch (_) {}
          }
          link.append(thumb);
          const caption = document.createElement('small');
          caption.textContent = this.dataset.productLabel;
          if (item.price != null && item.price !== '' && Number.isFinite(Number(item.price))) {
            try { caption.textContent = new Intl.NumberFormat(this.dataset.locale || 'en-CA', {style:'currency', currency:this.dataset.currency || 'CAD'}).format(Number(item.price)); } catch (_) {}
          }
          label.append(caption);
        }
        link.append(label);
        li.append(link);
        this.list.append(li);
      });
      this.all.href = this.searchURL(term);
      this.panel.hidden = false;
      this.fitResults(false);
      this.input.setAttribute('aria-expanded', 'true');
      this.status.textContent = this.list.getAttribute('aria-label') + ': ' + items.length;
    }
    key(event) {
      if (event.key === 'Escape') { this.cancel(); this.hide(); return; }
      if (this.panel.hidden || event.shiftKey) return;
      const options = [...this.list.children];
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.active = (this.active + step + options.length) % options.length;
        options.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.active)));
        this.input.setAttribute('aria-activedescendant', options[this.active].id);
        options[this.active].scrollIntoView({block:'nearest'});
      } else if (event.key === 'Enter' && this.active >= 0) {
        event.preventDefault();
        options[this.active].querySelector('a').click();
      }
    }
  });
}
